import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, FileText, MessageSquare, Sparkles, User } from 'lucide-react';

export const internalLearningAssistant: Playbook = {
  meta: {
    id: 'internal-learning-assistant',
    slug: 'internal-learning-assistant',
    categoryId: 'provide-support',
    name: 'The Internal L&D Genie',
    tagline: 'Support employee training and development.',
    description: 'Deploy a Genie that helps employees navigate training materials and understand company policies.',
    strategy: 'Deploy a Genie that helps employees navigate training materials, answers questions about specific training content, explains company policies, and provides on-demand support for learning initiatives. Available 24/7 to help employees understand and engage with training materials.',
    benefits: [
      'Get instant answers to training questions',
      'Access company policies 24/7',
      'Reduce training friction and confusion',
      'Ensure consistent information across teams'
    ],
    targetAudience: ['HR departments', 'Training teams', 'Large organizations', 'Remote teams'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'beginner',
    tags: ['training', 'learning', 'development', 'internal', 'hr', 'policies'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Internal L&D Genie - Employee Training Support with Voice Genies',
    metaDescription: 'Help employees understand training materials and company policies with an always-available learning Genie. Reduce training friction and improve completion rates.',
    keywords: ['employee training', 'learning and development', 'L&D Genie', 'training support', 'company policies', 'hr automation']
  },
  demoConfig: {
    triggerType: 'button',
    message: "👋 Hi! I'm your Learning & Development Genie. I can help you understand training materials, answer questions about company policies, or clarify any internal processes. What specific topic would you like help with today?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! I'm your L&D Genie. What training topic can I help you with today?" },
    { speaker: 'user', message: "I'm working through the data security training and don't understand encryption" },
    { speaker: 'agent', message: "I can help explain that! In our context, encryption is how we protect sensitive customer data by converting it into a coded format that can't be read without the proper key." },
    { speaker: 'user', message: "What specific encryption standards do we use?" },
    { speaker: 'agent', message: "According to our security policy, we use AES-256 encryption for data at rest and TLS 1.3 for data in transit. This ensures customer information stays protected throughout our systems." }
  ],
  templateConfig: {
    firstMessage: "👋 Hi! I'm your Learning & Development Genie. I can help explain training content, answer questions about company policies, or clarify internal processes. What specific topic would you like help with?",
    systemPrompt: "You are an internal Learning & Development Genie for employees. Help them understand specific training content, answer questions about company policies, explain procedures, and provide clarity on internal processes. Be clear, helpful, and knowledgeable about company training materials and policies.",
    tools: ['training_materials', 'policy_database', 'knowledge_base_search', 'email_capture'],
    responseTime: 'balanced'
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Employee has training question',
      description: 'Needs clarification on training content, policy details, or procedure steps'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Instant training support',
      description: 'Provides immediate access to the training Genie for specific questions'
    },
    {
      type: 'action',
      icon: MessageSquare,
      genieIcon: 'settings',
      label: 'Contextual explanation',
      description: 'Explains complex concepts in simple terms with relevant examples'
    },
    {
      type: 'action',
      icon: FileText,
      genieIcon: 'database',
      label: 'Policy clarification',
      description: 'Searches company handbook and provides accurate policy information'
    },
    {
      type: 'action',
      icon: Sparkles,
      genieIcon: 'share',
      label: 'Relevant content delivery',
      description: 'Shares specific training materials and resources related to the question'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Clear understanding achieved',
      description: 'Employee gets answers immediately and can continue with training effectively'
    }
  ],
  metrics: {
    responseTime: 'Instant',
    satisfactionScore: '4.7/5',
    timeToValue: '1 day',
    costSavings: 'Reduces trainer hours by 60%'
  },
  testimonials: [
    {
      companyName: 'GlobalTech Inc.',
      authorName: 'Michael Rodriguez',
      authorTitle: 'VP of Human Resources',
      quote: 'Our L&D Genie has transformed employee training. New hires get answers instantly instead of waiting for trainers, and our completion rates have increased significantly.',
      metrics: '60% reduction in training support tickets'
    },
    {
      companyName: 'FinanceFlow',
      authorName: 'Lisa Park',
      authorTitle: 'Learning & Development Manager',
      quote: 'The ability to provide 24/7 training support has been game-changing for our global team. Employees in different time zones can get help whenever they need it.',
      metrics: '45% faster onboarding time'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Setting Up Your Training Knowledge Base',
      description: 'How to organize training materials for optimal Genie responses',
      url: '/docs/training-knowledge-base'
    },
    {
      type: 'checklist',
      title: 'L&D Genie Launch Checklist',
      description: 'Everything you need to deploy your internal training Genie',
      url: '/resources/ld-launch-checklist'
    },
    {
      type: 'template',
      title: 'Common Training Q&A Templates',
      description: 'Pre-built responses for frequently asked training questions',
      url: '/templates/training-qa'
    }
  ]
};